import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Respuestas guardadas (Mensajería Etapa 2, 2026-08-20) ────────────────────
// Las consumen dos pantallas: Ajustes (donde se administran) y el composer de
// la bandeja (donde se insertan). Un solo hook para las dos — si cada una
// tuviera su propia query, editar una respuesta en Ajustes dejaría al composer
// mostrando la versión vieja hasta recargar.

export interface SavedReply {
  id: number;
  title: string;
  body: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

const KEY = ['saved-replies'];

export function useSavedReplies(enabled = true) {
  return useQuery<{ replies: SavedReply[] }>({
    queryKey: KEY,
    queryFn: () => api.get('/api/saved-replies'),
    enabled,
    // No cambian solas: solo se mueven cuando alguien las edita en esta misma
    // sesión, y esa mutación ya invalida la query.
    staleTime: 5 * 60_000,
  });
}

export function useCreateSavedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; body: string }) =>
      api.post<SavedReply>('/api/saved-replies', input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useUpdateSavedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number; title?: string; body?: string }) =>
      api.patch<SavedReply>(`/api/saved-replies/${id}`, patch),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

export function useDeleteSavedReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/saved-replies/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}

/** El servidor exige la lista COMPLETA de ids, no un movimiento relativo — ver
 *  reorderSavedReplies() en el server. Quien llame arma el array entero. */
export function useReorderSavedReplies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.post<{ replies: SavedReply[] }>('/api/saved-replies/reorder', { ids }),
    onSuccess: (data) => {
      // Se escribe la respuesta del servidor en vez de invalidar: el reorden es
      // la única acción donde el usuario hace varios clics seguidos, y un
      // refetch por clic hace que las filas parpadeen mientras se mueven.
      qc.setQueryData(KEY, data);
    },
  });
}
